import { supabase } from '@/lib/supabase'
import { logger } from '@/utils/logger'

export type AuditAction = 'REGISTER' | 'CANCEL' | 'MODIFY' | 'VIEW'

export interface AuditLogEntry {
  id: string
  timestamp: string
  action: AuditAction
  slotId?: string
  slotDate: string
  className: string
  userName?: string
  userEmail?: string
  ipAddress?: string
  userAgent?: string
  sessionId?: string
  oldValues?: Record<string, any>
  newValues?: Record<string, any>
  notes?: string
  createdAt: string
}

export interface AuditLogRequest {
  action: AuditAction
  slotId?: string
  slotDate: string
  className: string
  userName?: string
  userEmail?: string
  oldValues?: Record<string, any>
  newValues?: Record<string, any>
  notes?: string
}

class AuditService {
  private _sessionId: string
  private _userAgent: string
  
  constructor() {
    this._sessionId = this.generateSessionId()
    this._userAgent = this.getUserAgent()
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private getUserAgent(): string {
    if (typeof navigator !== 'undefined') {
      return navigator.userAgent
    }
    return 'Unknown'
  }

  private async getClientIP(): Promise<string> {
    return 'server-side-detected'
  }

  async logAction(request: AuditLogRequest): Promise<string | null> {
    try {
      const _ipAddress = await this.getClientIP()
      
      const { data, error } = await supabase
        .rpc('log_volunteer_action', {
          p_action: request.action,
          p_slot_id: request.slotId || null,
          p_slot_date: request.slotDate,
          p_class_name: request.className,
          p_user_name: request.userName || null,
          p_user_email: request.userEmail || null,
          p_old_values: request.oldValues || null,
          p_new_values: request.newValues || null,
          p_notes: request.notes || null
        })

      if (error) {
        console.error('Audit logging error:', error)
        return null
      }

      logger.info('Audit action logged', {
        component: 'AuditService',
        action: request.action,
        data: { className: request.className, slotDate: request.slotDate }
      })
      return data
    } catch (error) {
      console.error('Failed to log audit action:', error)
      return null
    }
  }

  async logRegistration(
    slotId: string,
    slotDate: string,
    className: string,
    userName: string,
    userEmail: string,
    notes?: string
  ): Promise<void> {
    await this.logAction({
      action: 'REGISTER',
      slotId,
      slotDate,
      className,
      userName,
      userEmail,
      newValues: {
        volunteerName: userName,
        volunteerEmail: userEmail,
        registeredAt: new Date().toISOString()
      },
      notes: notes || 'User registration via web interface'
    })
  }

  async logCancellation(
    slotId: string,
    slotDate: string,
    className: string,
    userName: string,
    userEmail: string,
    notes?: string
  ): Promise<void> {
    await this.logAction({
      action: 'CANCEL',
      slotId,
      slotDate,
      className,
      userName,
      userEmail,
      oldValues: {
        volunteerName: userName,
        volunteerEmail: userEmail
      },
      notes: notes || 'User cancellation via web interface'
    })
  }

  async logModification(
    slotId: string,
    slotDate: string,
    className: string,
    oldValues: Record<string, any>,
    newValues: Record<string, any>,
    notes?: string
  ): Promise<void> {
    await this.logAction({
      action: 'MODIFY',
      slotId,
      slotDate,
      className,
      oldValues,
      newValues,
      notes: notes || 'Slot modification via admin interface'
    })
  }

  async logView(
    slotDate: string,
    className?: string,
    notes?: string
  ): Promise<void> {
    await this.logAction({
      action: 'VIEW',
      slotDate,
      className: className || 'Unknown',
      notes: notes || 'User viewed volunteer slots'
    })
  }

  async getAuditLogs(
    limit: number = 100,
    offset: number = 0,
    filters?: {
      action?: AuditAction
      dateFrom?: string
      dateTo?: string
      className?: string
      userEmail?: string
    }
  ): Promise<{ data: AuditLogEntry[], count: number } | null> {
    try {
      let query = supabase
        .from('audit_log')
        .select('*', { count: 'exact' })
        .order('timestamp', { ascending: false })
        .range(offset, offset + limit - 1)

      if (filters?.action) {
        query = query.eq('action', filters.action)
      }
      if (filters?.dateFrom) {
        query = query.gte('slot_date', filters.dateFrom)
      }
      if (filters?.dateTo) {
        query = query.lte('slot_date', filters.dateTo)
      }
      if (filters?.className) {
        query = query.eq('class_name', filters.className)
      }
      if (filters?.userEmail) {
        query = query.ilike('user_email', `%${filters.userEmail}%`)
      }

      const { data, error, count } = await query

      if (error) {
        logger.error('Failed to fetch audit logs', {
          component: 'AuditService',
          data: error
        })
        return null
      }

      return {
        data: data || [],
        count: count || 0
      }
    } catch (error) {
      logger.error('Audit logs fetch error', {
        component: 'AuditService',
        data: error
      })
      return null
    }
  }

  async getAuditStats(dateFrom?: string, dateTo?: string): Promise<{
    totalActions: number
    registrations: number
    cancellations: number
    modifications: number
    views: number
    uniqueUsers: number
  } | null> {
    try {
      let query = supabase
        .from('audit_log')
        .select('action, user_email')

      if (dateFrom) {
        query = query.gte('slot_date', dateFrom)
      }
      if (dateTo) {
        query = query.lte('slot_date', dateTo)
      }

      const { data, error } = await query

      if (error || !data) {
        logger.error('Failed to fetch audit stats', {
          component: 'AuditService',
          data: error
        })
        return null
      }

      const stats = {
        totalActions: data.length,
        registrations: data.filter(item => item.action === 'REGISTER').length,
        cancellations: data.filter(item => item.action === 'CANCEL').length,
        modifications: data.filter(item => item.action === 'MODIFY').length,
        views: data.filter(item => item.action === 'VIEW').length,
        uniqueUsers: new Set(data.filter(item => item.user_email).map(item => item.user_email)).size
      }

      return stats
    } catch (error) {
      logger.error('Audit stats fetch error', {
        component: 'AuditService',
        data: error
      })
      return null
    }
  }

  async exportToCsv(filters?: {
    action?: AuditAction
    dateFrom?: string
    dateTo?: string
    className?: string
  }): Promise<string> {
    const result = await this.getAuditLogs(1000, 0, filters)
    
    if (!result?.data) {
      return ''
    }

    const headers = [
      'Timestamp',
      'Action',
      'Slot Date',
      'Class',
      'User Name',
      'User Email',
      'IP Address',
      'User Agent',
      'Notes'
    ].join(',')

    const rows = result.data.map(log => [
      log.timestamp,
      log.action,
      log.slotDate,
      log.className,
      log.userName || '',
      log.userEmail || '',
      log.ipAddress || '',
      log.userAgent || '',
      (log.notes || '').replace(/,/g, ';')
    ].map(field => `"${field}"`).join(','))

    return [headers, ...rows].join('\n')
  }
}

export const auditService = new AuditService()
export const logVolunteerAction = auditService.logAction.bind(auditService)
